import React, { useContext, useEffect, useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import { ExclamationTriangle } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import { Redirect, Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { validateEmail } from '../helpers/global';
import { UserContext } from '../context/UserContext';
import LanguagePicker from '../components/common/LanguagePicker';
import { BrandAside, LabeledInputField, authSubmitButtonClass, authLinkClass } from '../components/common/authUi';
import { createDefaultStrategies } from '../components/admin/manage-users/customer/strategies/helper';
import CircularProgressBar from '../components/common/circular-progress';

const emptyForm = { name: '', phone: '', email: '' };

const PublicSignup = () => {
  const { translate } = useContext(LocalizeContext);
  const { isUserLoggedIn } = useContext(UserContext);
  const history = useHistory();
  const { tag } = useParams();

  const [values, setValues] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyForm);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tagFound, setTagFound] = useState(tag ? null : true);
  const signupTag = tag || '/';

  useEffect(() => {
    if (!tag) return undefined;

    let cancelled = false;
    const findTag = async () => {
      const { response } = await makeApiRequests({
        endpoint: `${ENDPOINTS.PUBLIC_SIGNUP_CONFIG_EXISTS}?signupTag=${tag}`,
        method: 'GET'
      });
      if (!cancelled) setTagFound(Boolean(response?.exists));
    };

    findTag();
    return () => {
      cancelled = true;
    };
  }, [tag]);

  if (tagFound && isUserLoggedIn) return <Redirect from="/signup" to="/" />;

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setApiError('');
    const newErrors = {
      name: !values.name.trim() ? translate('name_required_short') : '',
      phone: !values.phone.trim() ? translate('phone_required_short') : '',
      email: !validateEmail(values.email) ? translate('invalid_email_short') : ''
    };

    if (Object.values(newErrors).some(err => err)) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await makeApiRequests({
        endpoint: ENDPOINTS.PUBLIC_SIGNUP,
        method: 'POST',
        requestBody: {
          ...values,
          strategies: createDefaultStrategies(),
          signupTag: signupTag
        }
      });

      if (error) {
        setApiError(error);
      } else {
        toast.success(translate('signup_success_verify_email'));
        history.push('/login');
      }
    } catch (err) {
      setApiError('An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh w-full flex-col overflow-x-hidden bg-brand-surface font-sans text-brand-navy lg:min-h-screen lg:flex-row lg:overflow-hidden">
      <BrandAside titleKey="signup_brand_headline" taglineKey="signup_brand_tagline" />

      {/* Main Content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:min-h-screen">
        <header className="flex w-full flex-wrap items-center justify-end gap-x-3 gap-y-3 border-b border-brand-navy/6 bg-brand-surface px-4 py-3.5 sm:gap-x-5 sm:px-[clamp(1.25rem,4vw,3rem)] sm:py-5">
          <div className="flex flex-shrink-0 items-center gap-4 sm:gap-6">
            <LanguagePicker />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col items-stretch justify-start px-4 py-6 sm:justify-center sm:px-[clamp(1.25rem,5vw,3rem)] sm:py-10 md:py-12 md:pb-16">
          <div className="w-full max-w-auth-form self-center">
            {tagFound == null ? (
              <div className="flex min-h-[40vh] items-center justify-center" role="status">
                <CircularProgressBar size={5} />
              </div>
            ) : !tagFound ? (
              <>
                <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight sm:mb-1 sm:text-4xl">
                  {translate('public_signup_heading')}
                </h1>
                <p className="m-0 max-w-prose text-sm leading-relaxed text-gray-500">
                  {translate('public_signup_config_invalid_info')}
                </p>
                <div role="alert" className="mb-3 mt-3">
                  <div className="flex items-start gap-2.5 sm:items-center sm:gap-3">
                    <span className="mt-0.5 inline-flex shrink-0 text-red-600 sm:mt-0" aria-hidden>
                      <ExclamationTriangle className="block h-5 w-5" />
                    </span>
                    <h2 className="mb-0 font-sans text-sm font-bold leading-snug text-red-800 sm:text-base">
                      {translate('public_signup_config_load_error')}
                    </h2>
                  </div>
                </div>
                <div className="mt-5 border-t border-brand-navy/8 pt-4 text-center">
                  <Link to="/login" className={authLinkClass}>
                    {translate('go_to_login_page')}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h1 className="mb-2 font-serif text-3xl font-semibold leading-tight tracking-tight sm:mb-3 sm:text-4xl">
                  {translate('public_signup_heading')}
                </h1>
                <p className="mb-2 text-sm text-gray-500 sm:mb-3">{translate('signup_all_fields_required')}</p>
                <p className="mb-8 max-w-prose text-sm leading-relaxed text-gray-600 sm:mb-10 sm:text-base">
                  {translate('public_signup_intro_hint')}
                </p>

                <form className="flex flex-col gap-3 md:gap-5 lg:gap-6" onSubmit={handleSubmit} noValidate>
                  <LabeledInputField
                    id="signup-name"
                    label={translate('name')}
                    type="text"
                    value={values.name}
                    error={errors.name}
                    onValueChange={v => handleChange('name', v)}
                    disabled={submitting}
                  />
                  <LabeledInputField
                    id="signup-phone"
                    label={translate('phone_number_public_signup')}
                    type="tel"
                    value={values.phone}
                    error={errors.phone}
                    onValueChange={v => handleChange('phone', v)}
                    disabled={submitting}
                  />
                  <LabeledInputField
                    id="signup-email"
                    label={translate('email')}
                    type="email"
                    value={values.email}
                    error={errors.email}
                    onValueChange={v => handleChange('email', v)}
                    disabled={submitting}
                  />
                  <button type="submit" className={authSubmitButtonClass} disabled={submitting}>
                    {translate('public_signup')}
                  </button>
                </form>

                {submitting && (
                  <ProgressBar
                    className="mt-5"
                    striped
                    animated
                    variant="dark"
                    now={100}
                    label={`${translate('creating_account')}...`}
                  />
                )}
                {apiError && (
                  <Alert variant="danger" className="mt-4 text-sm">
                    {apiError}
                  </Alert>
                )}

                <p className="mt-8 text-center text-sm sm:mt-10">
                  <Link
                    to="/login"
                    className={`${authLinkClass} ${submitting ? 'pointer-events-none text-muted' : ''}`}
                  >
                    {translate('already_have_an_account')}
                  </Link>
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PublicSignup;
